import {
    Container,
    FormControlLabel,
    Paper,
    Radio,
    RadioGroup,
    Typography,
    Select,
    InputLabel,
    FormControl,
    MenuItem,
    makeStyles,
    Table,
    TableRow,
    TableBody,
    TableCell,
    Button,
    TextField,
    Switch,
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import HtmlTooltip from "common/components/HtmlTooltip/HtmlTooltip";
import { SetLoading } from "common/components/Loader/Loader";
import { connect } from "common/connect";
import { changeUrl } from "common/history";
import Store from "common/store";
import StoreKeys from "common/storeKeys";
import * as React from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import EventBuilderService from "services/EventBuilderService";
import LookupService from "services/LookupService";
import { RewardTypes } from "common/constants/RewardTypes";
import { Colors } from "styles/Colors";
import "./EventCreator.scss";
// eslint-disable-next-line no-unused-vars
import { EventCreateRequestDto } from "common/types/EventCreateRequestDto";
// eslint-disable-next-line no-unused-vars
import { Lookup } from "common/types/Lookup";
import EventFormatService from "services/EventFormatService";
import { toMMDDYYYY, toMonthNameDayAndYear } from "common/helpers/dateHelper";
// eslint-disable-next-line no-unused-vars
import { SharedTournamentDto } from "common/types/SharedTournamentDto";
import EventMapper from "./EventMapper";

const propKeys = {
    createData: StoreKeys.EVENTCREATOR.CREATE_DATA,
    eventData: StoreKeys.EVENTCREATOR.EVENT_DATA,
    eventDetails: StoreKeys.EVENTCREATOR.EVENT_DETAILS,
    eventFormats: StoreKeys.EVENTCREATOR.EVENT_FORMATS,
    selectedEvent: StoreKeys.EVENTCREATOR.SELECTED_EVENT,
    upcomingEvents: StoreKeys.EVENTCREATOR.UPCOMING_EVENTS,
    games: StoreKeys.EVENTCREATOR.GAMES,
};

const wikiFromUrl = (url) => /liquipedia\.net\/([^/?#]+)/i.exec(url ?? "")?.[1];

const FIELD_GAP = 16;
const GAME_WIDTH = 200;
const DATE_WIDTH = 180;

const outlinedField = {
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: Colors.panelLighterColor,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: Colors.primaryDarker,
    },
};

const useStyles = makeStyles((theme) => ({
    searchArea: {
        display: "flex",
        flexDirection: "column",
        gap: FIELD_GAP,
        width: "fit-content",
        maxWidth: "100%",
        marginBottom: theme.spacing(4),
    },
    searchRow: {
        display: "flex",
        alignItems: "center",
        gap: FIELD_GAP,
        flexWrap: "wrap",
    },
    gameField: { ...outlinedField, width: GAME_WIDTH },
    dateField: { ...outlinedField, width: DATE_WIDTH },
    urlField: {
        ...outlinedField,
        width: GAME_WIDTH + 2 * DATE_WIDTH + 2 * FIELD_GAP,
        maxWidth: "100%",
    },
    selectInGrid: {
        minWidth: 200,
    },
    seriesCard: {
        padding: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },
    seriesOption: {
        marginLeft: 0,
        marginRight: 0,
    },
    fieldRow: {
        display: "flex",
        alignItems: "center",
    },
    narrowInput: {
        width: "33%",
        boxSizing: "border-box",
        margin: 0,
    },
    infoIcon: {
        fontSize: 18,
        marginLeft: 6,
        cursor: "help",
        verticalAlign: "middle",
        color: theme.palette.text.secondary,
    },
}));

/**
 * @typedef {Object} CreateData
 * @property {string} nameOverride
 * @property {number} rewardTypeId
 * @property {string} imageUrl
 * @property {number} eventId
 * @property {number} gameId
 * @property {number} eventFormatId
 * @property {boolean} hidden
 */

/**
 * @typedef {Object} EventCreatorProps
 * @property {CreateData} createData
 * @property {any} eventData
 * @property {any} eventDetails
 * @property {Array<Lookup>} eventFormats
 * @property {SharedTournamentDto} selectedEvent
 * @property {Array<SharedTournamentDto>} upcomingEvents
 * @property {Array<Lookup>} games
 */

function EventCreatorC(props) {
    /** @type {EventCreatorProps} */
    const {
        createData,
        eventData,
        eventDetails,
        eventFormats,
        selectedEvent,
        upcomingEvents,
        games,
    } = props;
    const now = new Date();
    const later = new Date();
    later.setDate(now.getDate() + 30);
    const classes = useStyles();
    const [fromDate, setFromDate] = React.useState(now);
    const [toDate, setToDate] = React.useState(later);
    const [additionalPages, setAdditionalPages] = React.useState("");
    const [useAdditionalPages, setUseAdditionalPages] = React.useState(false);
    const [eventUrl, setEventUrl] = React.useState("");

    React.useEffect(() => {
        Store.cacheChanges();
        Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, {});
        Store.set(StoreKeys.EVENTCREATOR.EVENT_DATA, null);
        Store.set(StoreKeys.EVENTCREATOR.SELECTED_EVENT, null);

        SetLoading(true, "Loading Lookups");

        Promise.all([
            LookupService.getGames().then((games) => {
                Store.set(StoreKeys.EVENTCREATOR.GAMES, games);
            }),
            EventFormatService.getAllFormats().then((formats) => {
                Store.set(StoreKeys.EVENTCREATOR.EVENT_FORMATS, formats);
            }),
        ]).then(() => {
            Store.applyChanges();
            SetLoading(false);
        });
    }, []);

    const onGameSelect = (event) => {
        const value = event.target.value;
        const gameId = Number(value);
        createData.gameId = gameId;
        Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
    };

    const onFromChange = (event) => {
        const value = new Date(event.target.value);
        if (!isNaN(value)) {
            setFromDate(value);
        }
    };

    const onToChange = (event) => {
        const value = new Date(event.target.value);
        if (!isNaN(value)) {
            setToDate(value);
        }
    };

    const gameIdForWiki = (wiki) => {
        if (!wiki) {
            return null;
        }

        return (
            games?.find(
                (game) =>
                    game.liquipediaWikiName?.toLowerCase() === wiki.toLowerCase()
            )?.id ?? null
        );
    };

    const onEventUrlChange = (event) => {
        const value = event.target.value;
        setEventUrl(value);

        const gameId = gameIdForWiki(wikiFromUrl(value));
        if (gameId && gameId !== createData?.gameId) {
            createData.gameId = gameId;
            Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
        }
    };

    const onSearch = () => {
        if (!createData?.gameId) {
            return;
        }

        SetLoading(true, "Loading Upcoming Events");
        EventBuilderService.getUpcomingEvents(
            createData.gameId,
            fromDate,
            toDate
        ).then((events) => {
            events.forEach((e) => {
                e.start = e.start.replace("Z", "");
            });
            Store.set(StoreKeys.EVENTCREATOR.UPCOMING_EVENTS, events);
            Store.set(StoreKeys.EVENTCREATOR.SELECTED_EVENT, null);
            SetLoading(false);
        });
    };

    const onEventFormatSelect = (event) => {
        const value = event.target.value;
        createData.eventFormatId = Number(value);
        Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
    };

    const eventNameChange = (event) => {
        const value = event.target.value;
        createData.nameOverride = value;
        Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
    };

    const hiddenChange = (event) => {
        createData.hidden = event.target.checked;
        Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
    };

    const rewardTypeChange = (event) => {
        const value = event.target.value;
        createData.rewardTypeId = value;
        Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
    };

    const imageUrlChange = (event) => {
        const value = event.target.value;
        createData.imageUrl = value;
        Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
    };

    const loadEventPressed = async () => {
        SetLoading(true, "Loading Event Details");

        try {
            const eventDetails = await EventBuilderService.getEventDetails(
                createData.gameId,
                createData.eventId,
                additionalPages
            );
            Store.set(StoreKeys.EVENTCREATOR.EVENT_DETAILS, eventDetails);
            SetLoading(false);
        } catch (error) {
            console.log(error);
            SetLoading(false);
            toast("Contact Lor!", { type: "error" });
        }
    };

    /**
     *
     * @param {number} tournamentId
     * @param {Array<SharedTournamentDto>} upcomingEvents
     */
    const applySelectedTournament = (tournament) => {
        createData.eventId = tournament.id;
        createData.rewardTypeId = getRewardType(tournament.title);
        createData.nameOverride = tournament.title;
        createData.imageUrl = tournament.bannerUrl;

        const changes = {};
        changes[StoreKeys.EVENTCREATOR.SELECTED_EVENT] = tournament;
        changes[StoreKeys.EVENTCREATOR.CREATE_DATA] = createData;
        changes[StoreKeys.EVENTCREATOR.EVENT_DETAILS] = null;
        Store.setMany(changes);
    };

    const selectEvent = (tournamentId, upcomingEvents) => {
        applySelectedTournament(
            upcomingEvents.find((s) => s.id === Number(tournamentId))
        );
    };

    const onLoadByUrl = () => {
        const wiki = wikiFromUrl(eventUrl);
        if (!wiki) {
            toast("Paste a full Liquipedia event URL.", { type: "error" });
            return;
        }

        const gameId = gameIdForWiki(wiki);
        if (!gameId) {
            toast(`No Pickstop game for the "${wiki}" wiki.`, { type: "error" });
            return;
        }

        if (gameId !== createData?.gameId) {
            createData.gameId = gameId;
            Store.set(StoreKeys.EVENTCREATOR.CREATE_DATA, createData);
        }

        SetLoading(true, "Resolving Tournament");
        EventBuilderService.getTournamentByUrl(gameId, eventUrl)
            .then((tournament) => {
                SetLoading(false);
                if (!tournament) {
                    toast("No tier 1/2 tournament found for that URL.", {
                        type: "error",
                    });
                    return;
                }
                applySelectedTournament(tournament);
            })
            .catch((error) => {
                console.log(error);
                SetLoading(false);
                toast("Failed to resolve URL.", { type: "error" });
            });
    };

    const renderSearch = () => {
        return (
            <div className={classes.searchArea}>
                <div className={classes.searchRow}>
                    <TextField
                        select
                        label="Game"
                        variant="outlined"
                        className={classes.gameField}
                        value={createData?.gameId ?? ""}
                        onChange={onGameSelect}
                    >
                        {games?.map((game) => (
                            <MenuItem value={game.id} key={game.id}>
                                {game.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        id="from-date"
                        label="From"
                        type="date"
                        variant="outlined"
                        className={classes.dateField}
                        value={toMMDDYYYY(fromDate)}
                        onChange={onFromChange}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { colorScheme: "dark" } }}
                    />
                    <TextField
                        id="to-date"
                        label="To"
                        type="date"
                        variant="outlined"
                        className={classes.dateField}
                        value={toMMDDYYYY(toDate)}
                        onChange={onToChange}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ style: { colorScheme: "dark" } }}
                    />
                    <Button variant="contained" color="primary" onClick={onSearch}>
                        Search
                    </Button>
                </div>
                <div className={classes.searchRow}>
                    <TextField
                        label="or paste a Liquipedia event URL"
                        variant="outlined"
                        className={classes.urlField}
                        value={eventUrl}
                        onChange={onEventUrlChange}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onLoadByUrl}
                    >
                        Load by URL
                    </Button>
                </div>
            </div>
        );
    };

    const onEventChange = (event) => {
        const seriesId = event.target.value;
        selectEvent(seriesId, upcomingEvents);
    };

    const renderSeriesSelection = () => {
        return (
            <Paper className={classes.seriesCard} elevation={3}>
                <Typography variant="subtitle1" gutterBottom>
                    Upcoming Tournaments
                </Typography>
                <RadioGroup
                    aria-label="series"
                    name="series"
                    value={selectedEvent?.id ?? ""}
                    onChange={onEventChange}
                >
                    {upcomingEvents.map((e) => {
                        return (
                            <FormControlLabel
                                key={e.id}
                                className={classes.seriesOption}
                                value={e.id}
                                control={<Radio />}
                                label={
                                    <>
                                        {e.title} (
                                        <a
                                            href={e.wikiLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            link
                                        </a>
                                        )
                                    </>
                                }
                            />
                        );
                    })}
                </RadioGroup>
            </Paper>
        );
    };

    const getRewardType = (name) => {
        name = name.toLowerCase();
        if (name.includes("regional")) return RewardTypes.Medal;
        else if (name.includes("major")) return RewardTypes.Trophy;
        else if (name.includes("championship")) return RewardTypes.Globe;
    };

    const additionalPagesToggle = (event) => {
        const enabled = event.target.checked;
        setUseAdditionalPages(enabled);
        if (!enabled) setAdditionalPages("");
    };

    const renderLoadButton = () => {
        return (
            <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 8, maxWidth: 640 }}>
                    <div className={classes.fieldRow}>
                        <FormControlLabel
                            label="Additional Liquipedia pages"
                            control={
                                <Switch
                                    checked={useAdditionalPages}
                                    onChange={additionalPagesToggle}
                                    color="primary"
                                />
                            }
                        />
                        <HtmlTooltip title="One per line or comma-separated, page name or full URL. Use when brackets live on a subpage, e.g. a closed qualifier.">
                            <InfoOutlined className={classes.infoIcon} />
                        </HtmlTooltip>
                    </div>
                    {useAdditionalPages && (
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            value={additionalPages}
                            onChange={(e) => setAdditionalPages(e.target.value)}
                            placeholder="BLAST/Bounty/2026/Summer/Qualifier"
                        />
                    )}
                </div>
                <button onClick={loadEventPressed}>Load Event</button>
            </div>
        );
    };

    const onCreate = async (mapperState) => {
        const stageMapping = { ...mapperState };
        for (const stageIndex in stageMapping.stages) {
            const stage = stageMapping.stages[stageIndex];
            stage.matchMappingKeys = stage.matchMappingKeys.map(
                (key) => `${key.liquipediaPageId}:|:${key.liquipediaPage}:|:${key.id}`
            );
            stage.standingsMappingKeys = stage.standingsMappingKeys.map(
                (key) => `${key.liquipediaPageId}:|:${key.liquipediaPage}:|:${key.id}`
            );
        }

        SetLoading(true, "Creating Event");
        /** @type {EventCreateRequestDto} */
        const createRequest = {
            tournamentId: selectedEvent.id,
            liquipediaUrl: createData.liquipediaUrl,
            imageUrl: createData.imageUrl,
            rewardType: createData.rewardTypeId,
            gameId: createData.gameId,
            nameOverride: createData.nameOverride,
            hidden: !!createData.hidden,
            eventFormatId: createData.eventFormatId,
            stageMapping: stageMapping,
            liquipediaData: eventDetails.liquipediaData
        };

        try {
            await EventBuilderService.createEvent(createRequest);
            SetLoading(false);
            toast("Event Created!", { type: "success" });
            changeUrl("/admin/eventManager");
        } catch (error) {
            console.log(error);
            SetLoading(false);
            toast("Contact Ben!", { type: "error" });
        }
    };

    const renderTeams = () => {
        const teams = eventData.teams;

        return teams.map((team, i) => {
            return (
                <div key={i}>
                    <img
                        src={team.logoUrl}
                        alt="Team Logo"
                        style={{ width: 120, height: 50 }}
                    />
                    <h5>{`${team.name} [${team.abbreviation}]`}</h5>
                    <table>
                        <tbody>
                            {team.members.map((teamMember, index) => {
                                return (
                                    <tr key={index}>
                                        <td>
                                            <img
                                                src={teamMember.flag.imageUrl}
                                                style={{
                                                    width: 24,
                                                    height: 24,
                                                }}
                                                title={
                                                    teamMember.flag.nationality
                                                }
                                                alt={
                                                    teamMember.flag.nationality
                                                }
                                            />
                                        </td>
                                        <td>{teamMember.username}</td>
                                        <td>{teamMember.realName}</td>
                                        <td>{teamMember.position}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <hr />
                </div>
            );
        });
    };

    const renderSelectedEvent = () => {
        return (
            <>
                <hr />
                <h1>{selectedEvent.title}</h1>
                <div>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell colSpan={2}>
                                    <div className={classes.fieldRow}>
                                        <input
                                            className={classes.narrowInput}
                                            value={createData?.nameOverride ?? ""}
                                            onChange={eventNameChange}
                                        />
                                        <HtmlTooltip title="You do not need to include the year in the name.">
                                            <InfoOutlined className={classes.infoIcon} />
                                        </HtmlTooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell colSpan={2}>
                                    <div className={classes.fieldRow}>
                                        <input
                                            className={classes.narrowInput}
                                            value={createData?.imageUrl ?? ""}
                                            onChange={imageUrlChange}
                                        />
                                        <HtmlTooltip title="Visual image (without event title) is ideal.">
                                            <InfoOutlined className={classes.infoIcon} />
                                        </HtmlTooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Image Preview</TableCell>
                                <TableCell colSpan={2}>
                                    <img
                                        src={createData?.imageUrl}
                                        alt=""
                                        style={{ maxWidth: 300 }}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Event Format</TableCell>
                                <TableCell colSpan={2}>
                                    <FormControl
                                        className={classes.selectInGrid}
                                    >
                                        <InputLabel id={"event-format"}>
                                            Event Format
                                        </InputLabel>
                                        <Select
                                            labelId={"event-format"}
                                            value={
                                                createData?.eventFormatId ?? ""
                                            }
                                            onChange={onEventFormatSelect}
                                            color="primary"
                                            disabled={!!eventDetails}
                                        >
                                            {eventFormats.map((eventFormat) => (
                                                <MenuItem
                                                    key={eventFormat.id}
                                                    value={eventFormat.id}
                                                >
                                                    {eventFormat.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Reward Type</TableCell>
                                <TableCell colSpan={2}>
                                    <FormControl
                                        className={classes.selectInGrid}
                                    >
                                        <InputLabel id={"reward-type"}>
                                            Reward Type
                                        </InputLabel>
                                        <Select
                                            labelId={"reward-type"}
                                            value={
                                                createData?.rewardTypeId ?? ""
                                            }
                                            onChange={rewardTypeChange}
                                            color="primary"
                                        >
                                            {Object.keys(
                                                RewardTypes.getRewardTypes(
                                                    createData.gameId
                                                )
                                            ).map((key) => (
                                                <MenuItem
                                                    value={RewardTypes[key]}
                                                    key={RewardTypes[key]}
                                                >
                                                    {key}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Hidden</TableCell>
                                <TableCell colSpan={2}>
                                    <div className={classes.fieldRow}>
                                        <Switch
                                            checked={!!createData?.hidden}
                                            onChange={hiddenChange}
                                            color="primary"
                                        />
                                        <HtmlTooltip title="When on, the event is created hidden: users won't see it anywhere. Open or unhide it later from the Event Manager.">
                                            <InfoOutlined className={classes.infoIcon} />
                                        </HtmlTooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Start Time</TableCell>
                                <TableCell colSpan={2}>
                                    {toMonthNameDayAndYear(selectedEvent.start)}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>End Time</TableCell>
                                <TableCell colSpan={2}>
                                    {toMonthNameDayAndYear(selectedEvent.end)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </>
        );
    };

    return (
        <Container className="event-creator" maxWidth={false}>
            <Helmet>
                <title>Pickstop - Event Creator</title>
            </Helmet>
            <h1>Event Creator</h1>
            {renderSearch()}
            {upcomingEvents?.length > 0 && renderSeriesSelection()}
            {selectedEvent && renderSelectedEvent()}
            {selectedEvent &&
                createData?.gameId &&
                createData?.eventFormatId &&
                createData?.rewardTypeId &&
                !eventDetails &&
                renderLoadButton()}
            {eventDetails && (
                <>
                    <hr />
                    <EventMapper
                        details={eventDetails}
                        format={eventFormats.find(
                            (f) => f.id === createData?.eventFormatId
                        )}
                        onCreate={onCreate}
                    />
                </>
            )}

            {eventData && <div style={{ marginTop: 24 }}>{renderTeams()}</div>}
        </Container>
    );
}

function EventCreator(props) {
    return connect(<EventCreatorC />, propKeys, props);
}

export default EventCreator;
